import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map } from 'lodash';
import { map as rxMap, Observable, of } from 'rxjs';

import {
  Contact,
  EndorsementCreate,
  EnquiryConfig,
  InsurerSummary,
  PatchQuoteResponse,
  PaymentType,
  Quote,
  QUOTE_STATUSES,
  QUOTE_WORKFLOW_STATUSES,
  QuoteAddon,
  QuoteAddonOptions,
  QuoteAddonUpdate,
  QuoteAdjustment,
  QuoteLite,
  QuoteOption,
  QuotePermissions,
  QuoteStatus,
  QuoteStatusUpdate,
  QuoteTierOptions,
  QuoteTierRate,
  QuoteTierRateUpdate,
  QuoteWorkflowStatus,
} from '@lockton-apps/shared/interfaces';
import { ClassOfBusinessId } from '@lockton-apps/shared/util/constants';
import { ProposalMocksService } from '@lockton-apps/shared/util/proposal-utils';
import {
  DISPATCH_ACTIONS,
  DispatchAction,
  PatchInitializeData,
  ProposalSubmitData,
  ProposalUpdateData,
} from '../utils/patch-actions';
import { getDevConfig } from '@lockton-apps/shared/util/helpers';

export type RawPatchQuoteResponse = PatchQuoteResponse & {
  permissions: QuotePermissions;
};

@Injectable({
  providedIn: 'root',
})
export class Quotes2Service {
  constructor(
    @Inject('BASE_API_URL_V2') private baseUrlV2: string,
    private http: HttpClient,
    private proposalMocksService: ProposalMocksService
  ) {}

  /** Be careful using this as it is costly */
  getQuotesWithProp(): Observable<Quote[]> {
    return this.http
      .get<Array<{ quote: Quote; permissions: QuotePermissions }>>(
        `${this.baseUrlV2}/quote`
      )
      .pipe(
        rxMap((res) =>
          map(res, (qp) => ({
            ...qp.quote,
            permissions: qp.permissions,
            workflowStatus:
              qp.quote.workflowStatus === null
                ? this.mapStatusToWorkflowStatus(qp.quote.status)
                : qp.quote.workflowStatus,
          }))
        )
      );
  }

  getQuotes(): Observable<QuoteLite[]> {
    return this.http
      .get<Array<{ quote: QuoteLite; permissions: QuotePermissions }>>(
        `${this.baseUrlV2}/quote/dashboard`
      )
      .pipe(
        rxMap((res) =>
          map(res, (qp) => ({
            ...qp.quote,
            permissions: qp.permissions,
            workflowStatus:
              qp.quote.workflowStatus === null
                ? this.mapStatusToWorkflowStatus(qp.quote.status)
                : qp.quote.workflowStatus,
          }))
        )
      );
  }

  getQuote(id: number): Observable<Quote> {
    return this.http
      .get<{ quote: Quote; permissions: QuotePermissions }>(
        `${this.baseUrlV2}/quote/${id}`
      )
      .pipe(
        rxMap((qp) => ({
          ...qp.quote,
          permissions: qp.permissions,
          workflowStatus:
            qp.quote.workflowStatus === null
              ? this.mapStatusToWorkflowStatus(qp.quote.status)
              : qp.quote.workflowStatus,
        }))
      );
  }

  getQuoteByProposalId(
    proposalId: string,
    portalId: number,
    configId: number,
    clientId: number
  ): Observable<Quote> {
    if (getDevConfig('proposalForm.mockData')) {
      return of(
        this.proposalMocksService.createMockedQuote(
          proposalId,
          portalId,
          configId,
          clientId
        )
      );
    }

    return this.http
      .get<{ quote: Quote; permissions: QuotePermissions }>(
        `${this.baseUrlV2}/proposal/${proposalId}/quote`
      )
      .pipe(
        rxMap((qp) => ({
          ...qp.quote,
          permissions: qp.permissions,
          workflowStatus:
            qp.quote.workflowStatus === null
              ? this.mapStatusToWorkflowStatus(qp.quote.status)
              : qp.quote.workflowStatus,
        }))
      );
  }

  getQuotesByClientId(clientId: number): Observable<Quote[]> {
    return this.http
      .get<Array<{ quote: Quote; permissions: QuotePermissions }>>(
        `${this.baseUrlV2}/client/${clientId}/quote`
      )
      .pipe(
        rxMap((res) =>
          map(res, (qp) => ({
            ...qp.quote,
            permissions: qp.permissions,
            workflowStatus:
              qp.quote.workflowStatus === null
                ? this.mapStatusToWorkflowStatus(qp.quote.status)
                : qp.quote.workflowStatus,
          }))
        )
      );
  }

  // TODO: move to v2
  initialise(config: EnquiryConfig): Observable<Quote[]> {
    return this.http
      .post<{ quotes: { quote: Quote; permissions: QuotePermissions }[] }>(
        `${this.baseUrlV2}/quote`,
        config
      )
      .pipe(
        rxMap((res) =>
          map(res.quotes, (qp) => ({
            ...qp.quote,
            permissions: qp.permissions,
            workflowStatus:
              qp.quote.workflowStatus === null
                ? this.mapStatusToWorkflowStatus(qp.quote.status)
                : qp.quote.workflowStatus,
          }))
        )
      );
  }

  assignLeadBroker(quoteId: number, userId: string | null) {
    return this.http.patch(`${this.baseUrlV2}/quote/assign`, {
      quoteId,
      userId,
    });
  }

  assignContacts(quoteId: number, userIds: string[]): Observable<Contact[]> {
    return this.http.put<Contact[]>(
      `${this.baseUrlV2}/quote/${quoteId}/contacts`,
      userIds
    );
  }

  release(quoteId: number): Observable<Quote> {
    return this.http.patch<Quote>(
      `${this.baseUrlV2}/quote/${quoteId}/release`,
      {}
    );
  }

  approve(quoteId: number, comment: string): Observable<Quote> {
    return this.http.patch<Quote>(`${this.baseUrlV2}/quote/approve`, {
      quoteId,
      comment,
    });
  }

  updateStatus(update: QuoteStatusUpdate): Observable<Quote> {
    return this.http.patch<Quote>(`${this.baseUrlV2}/quote/status`, update);
  }

  updateAddon(payload: QuoteAddonUpdate): Observable<QuoteAddon> {
    return this.http.put<QuoteAddon>(`${this.baseUrlV2}/quote/addon`, payload);
  }

  updateAdjustment(
    quoteId: number,
    data: QuoteAdjustment
  ): Observable<RawPatchQuoteResponse> {
    return this.http
      .patch<RawPatchQuoteResponse>(`${this.baseUrlV2}/quote`, {
        quoteId,
        action: DISPATCH_ACTIONS.AdjustmentUpdate,
        data,
      })
      .pipe(
        rxMap((res) => ({
          ...res,
          quote: {
            ...res.quote,
            permissions: res.permissions,
            workflowStatus:
              res.quote.workflowStatus === null
                ? this.mapStatusToWorkflowStatus(res.quote.status)
                : res.quote.workflowStatus,
          },
        }))
      );
  }

  updateEndorsement(
    quoteId: number,
    endorsements: EndorsementCreate[]
  ): Observable<RawPatchQuoteResponse> {
    return this.http
      .patch<RawPatchQuoteResponse>(`${this.baseUrlV2}/quote`, {
        quoteId,
        action: DISPATCH_ACTIONS.EndorsementUpdate,
        data: { endorsements },
      })
      .pipe(
        rxMap((res) => ({
          ...res,
          quote: {
            ...res.quote,
            permissions: res.permissions,
            workflowStatus:
              res.quote.workflowStatus === null
                ? this.mapStatusToWorkflowStatus(res.quote.status)
                : res.quote.workflowStatus,
          },
        }))
      );
  }

  updateOption(payload: QuoteOption): Observable<QuoteOption> {
    return this.http.put<QuoteOption>(
      `${this.baseUrlV2}/quote/option`,
      payload
    );
  }

  getOptionsForClassOfBusiness(
    quoteId: number,
    cob: ClassOfBusinessId
  ): Observable<QuoteTierOptions[]> {
    return this.http.get<QuoteTierOptions[]>(
      `${this.baseUrlV2}/quote/${quoteId}/class-of-business/${cob}/option`
    );
  }

  getOptions(
    quoteId: number
  ): Observable<Record<ClassOfBusinessId, QuoteAddonOptions>> {
    return this.http.get<Record<ClassOfBusinessId, QuoteAddonOptions>>(
      `${this.baseUrlV2}/quote/${quoteId}/option`
    );
  }

  getInsurer(quoteId: number): Observable<InsurerSummary> {
    return this.http.get<InsurerSummary>(
      `${this.baseUrlV2}/quote/${quoteId}/insurer`
    );
  }

  getRate(payload: QuoteTierRateUpdate): Observable<QuoteTierRate> {
    return this.http.put<QuoteTierRate>(
      `${this.baseUrlV2}/addon/rate`,
      payload
    );
  }

  updateChecklist(
    quoteId: number,
    checklist: Record<string, boolean>
  ): Observable<Quote> {
    return this.http.put<Quote>(
      `${this.baseUrlV2}/quote/${quoteId}/checklist`,
      checklist
    );
  }

  // V2 API

  reconfigure(
    quoteId: number,
    data: PatchInitializeData
  ): Observable<PatchQuoteResponse> {
    return this.patch<PatchInitializeData>(
      quoteId,
      DISPATCH_ACTIONS.PatchInitialized,
      data
    );
  }

  sendRenewalInvite(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.SendRenewalInvite);
  }

  proposalUpdate(
    quoteId: number,
    data: ProposalUpdateData
  ): Observable<PatchQuoteResponse> {
    if (this.proposalMocksService.isUsingMocks) {
      return this.proposalMocksService
        .saveProposalLocally({ id: data.proposalId, ...data })
        .pipe(
          rxMap((proposal) => ({
            responses: [],
            quote: {
              proposal,
            } as Quote,
          }))
        );
    }

    return this.patch<ProposalUpdateData>(
      quoteId,
      DISPATCH_ACTIONS.ProposalUpdate,
      data
    );
  }

  proposalSubmit(
    quoteId: number,
    data: ProposalSubmitData = {} as ProposalSubmitData
  ): Observable<PatchQuoteResponse> {
    if (this.proposalMocksService.isUsingMocks) {
      return this.proposalMocksService
        .saveProposalLocally({ id: data.proposalId, ...data })
        .pipe(
          rxMap((proposal) => ({
            responses: [],
            quote: {
              proposal,
            } as Quote,
          }))
        );
    }

    return this.patch<ProposalSubmitData>(
      quoteId,
      DISPATCH_ACTIONS.ProposalSubmit,
      data
    );
  }

  return(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.Return);
  }

  requote(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.Requote);
  }

  hold(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.Hold);
  }

  send(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.Send);
  }

  accept(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.Accept);
  }

  invoice(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.Invoice);
  }

  paymentReceived(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.PaymentReceived);
  }

  complete(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.Complete);
  }

  request(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.QuoteRequest);
  }

  ready(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.QuoteReady);
  }

  notTakenUp(quoteId: number): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.NotTakenUp);
  }

  setPaymentType(
    quoteId: number,
    paymentType: PaymentType
  ): Observable<PatchQuoteResponse> {
    return this.patch(quoteId, DISPATCH_ACTIONS.SetPaymentType, {
      paymentType,
    });
  }

  private patch<TData>(
    quoteId: number,
    action: DispatchAction,
    data?: TData
  ): Observable<RawPatchQuoteResponse> {
    return this.http
      .patch<RawPatchQuoteResponse>(`${this.baseUrlV2}/quote`, {
        quoteId,
        action,
        data,
      })
      .pipe(
        rxMap((res) => ({
          ...res,
          quote: {
            ...res.quote,
            permissions: res.permissions,
            workflowStatus:
              res.quote.workflowStatus === null
                ? this.mapStatusToWorkflowStatus(res.quote.status)
                : res.quote.workflowStatus,
          },
        }))
      );
  }

  private mapStatusToWorkflowStatus(status: QuoteStatus): QuoteWorkflowStatus {
    switch (status) {
      case QUOTE_STATUSES.Initialized:
        return QUOTE_WORKFLOW_STATUSES.NewProposal;
      case QUOTE_STATUSES.FormSent:
        return QUOTE_WORKFLOW_STATUSES.ProposalSent;
      case QUOTE_STATUSES.FormIncomplete:
        return QUOTE_WORKFLOW_STATUSES.ProposalInProgress;
      case QUOTE_STATUSES.FormComplete:
        return QUOTE_WORKFLOW_STATUSES.ProposalComplete;
      case QUOTE_STATUSES.OnHold:
        return QUOTE_WORKFLOW_STATUSES.QuoteOnHold;
      case QUOTE_STATUSES.HealthEvaluationRequired:
        return QUOTE_WORKFLOW_STATUSES.QuoteHE;
      case QUOTE_STATUSES.HealthEvaluationActive:
        return QUOTE_WORKFLOW_STATUSES.QuoteHE;
      case QUOTE_STATUSES.Quoted:
        return QUOTE_WORKFLOW_STATUSES.QuoteReady;
      case QUOTE_STATUSES.BrokerApproved:
        return QUOTE_WORKFLOW_STATUSES.QuoteSent;
      case QUOTE_STATUSES.QuoteAccepted:
        return QUOTE_WORKFLOW_STATUSES.QuoteAccepted;
      case QUOTE_STATUSES.AwaitingPayment:
        return QUOTE_WORKFLOW_STATUSES.AwaitingPayment;
      case QUOTE_STATUSES.Complete:
        return QUOTE_WORKFLOW_STATUSES.PolicyDocumentationSent;
      default:
        return QUOTE_WORKFLOW_STATUSES.NewProposal;
    }
  }
}
